import { useRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from '@/icons/Icon';
import { parseLocalDateKey, withinPaginationWindow, type SlotGroup } from './slot-utils';

type Props = {
  groups: SlotGroup[];
  selectedDateKey: string | null;
  onSelectDate: (dateKey: string) => void;
  /** Anchors the 30-day pagination window (the earliest date first fetched). */
  firstFetchedDate: Date;
  /** True once the most recently fetched page came back empty — stops further pagination. */
  lastPageWasEmpty: boolean;
  /** Requests the next page of dates. Called at most once per "near the end" scroll/click. */
  onLoadMore: () => void;
  className?: string;
  style?: React.CSSProperties;
};

/** Approximate width of one date chip, used both as the scroll step and the "near the end" threshold. */
const CHIP_SCROLL_PX = 160;

/**
 * Horizontal date-chip carousel for the onboarding Calendly slot picker.
 * Purely presentational — pagination decisions (whether more dates can be
 * fetched) are computed here from `slot-utils`' pure helpers, but the actual
 * fetch is owned by the parent via `onLoadMore`.
 */
export function DateCarousel({
  groups,
  selectedDateKey,
  onSelectDate,
  firstFetchedDate,
  lastPageWasEmpty,
  onLoadMore,
  className,
  style,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  function scrollByAmount(amount: number) {
    // `?.scrollBy` (not just `?.current`) guards jsdom, which doesn't
    // implement element-level scrolling — a hard throw here would also kill
    // the `maybeLoadMore()` call right after it in `handleRightClick`.
    scrollRef.current?.scrollBy?.({ left: amount, behavior: 'smooth' });
  }

  function maybeLoadMore() {
    const el = scrollRef.current;
    if (!el || lastPageWasEmpty) return;
    const distanceFromEnd = el.scrollWidth - (el.scrollLeft + el.clientWidth);
    if (distanceFromEnd > CHIP_SCROLL_PX) return;

    const lastGroup = groups[groups.length - 1];
    const candidateDate = lastGroup ? parseLocalDateKey(lastGroup.dateKey) : firstFetchedDate;
    if (!withinPaginationWindow(firstFetchedDate, candidateDate)) return;

    onLoadMore();
  }

  function handleRightClick() {
    scrollByAmount(CHIP_SCROLL_PX);
    maybeLoadMore();
  }

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)} style={style}>
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className="h-8 w-8 shrink-0 rounded-full"
        aria-label="Show earlier dates"
        onClick={() => scrollByAmount(-CHIP_SCROLL_PX)}
      >
        <ChevronLeft size={16} />
      </Button>

      <div
        ref={scrollRef}
        className="flex min-w-0 flex-1 gap-2 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onScroll={maybeLoadMore}
      >
        {groups.map((group) => {
          const isSelected = group.dateKey === selectedDateKey;
          return (
            <button
              key={group.dateKey}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelectDate(group.dateKey)}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                isSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-card-foreground hover:bg-accent',
              )}
            >
              {group.label}
            </button>
          );
        })}
      </div>

      <Button
        type="button"
        size="icon"
        variant="secondary"
        className="h-8 w-8 shrink-0 rounded-full"
        aria-label="Show later dates"
        onClick={handleRightClick}
      >
        <ChevronRight size={16} />
      </Button>
    </div>
  );
}
